import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import { Cantiere, Cluster } from '../types';
import { useNotification } from '../components/NotificationContext';

interface UserDashboardProps {
  onSelectCantiere: (cantiere: Cantiere) => void;
  isAdmin: boolean;
}

const PieChart: React.FC<{ data: { [key: string]: number } }> = ({ data }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>Nessun oggetto</div>;

  const colors = {
    'Non Lottizzato': '#e57373', // Reddish
    'Draft': '#bdbdbd',
    'In lavorazione': '#ffb74d',
    'Completo': '#81c784',
    'Collaudato': '#2e7d32', // Darker green
    'Errore Mets-Ecomic': '#b00020', // Error red
    'Pacchettizzato': '#1976d2'  // Blue
  };

  let cumulativePercent = 0;

  function getCoordinatesForPercent(percent: number) {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  }

  const slices = Object.entries(data).map(([label, value]) => {
    if (value === 0) return null;
    const percent = value / total;
    const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
    cumulativePercent += percent;
    const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
    const largeArcFlag = percent > 0.5 ? 1 : 0;
    const pathData = [
      `M ${startX} ${startY}`,
      `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      `L 0 0`,
    ].join(' ');
    return <path key={label} d={pathData} fill={colors[label as keyof typeof colors] || '#ccc'} />;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '20px 0' }}>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {Object.entries(data).map(([label, value]) => (
          value > 0 && (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors[label as keyof typeof colors] }} />
              <span style={{ fontWeight: '500' }}>{label}: {value}</span>
            </div>
          )
        ))}
      </div>
      <svg viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)', width: '150px', height: '150px' }}>
        {slices}
      </svg>
    </div>
  );
};

interface GlobalStats {
  id: number | string;
  name: string;
  cluster_id?: number | null;
  cluster_name?: string | null;
  total_objects: number;
  total_files: number; // Sum of masters
  total_tiff: number;
  total_raf: number;
  total_jpegs: number;
  total_packages: number;
  total_prototypes: number;
  total_sent: number;
  total_archived: number;
  digital_resource_value: number;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ onSelectCantiere, isAdmin }) => {
  const { notify } = useNotification();
  const [cantieri, setCantieri] = useState<Cantiere[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [selectedClusterFilter, setSelectedClusterFilter] = useState<string>('all');

  // Compute cluster stats from globalStats joined with clusters
  const clusterStats = useMemo(() => {
    return clusters.map(cluster => {
      const clusterCantiereIds = new Set(cluster.cantieri.map(c => String(c.id)));
      const relatedStats = globalStats.filter(gs => clusterCantiereIds.has(String(gs.id)));
      const totalTiff = relatedStats.reduce((sum, s) => sum + s.total_tiff, 0);
      const totalObjects = relatedStats.reduce((sum, s) => sum + s.total_objects, 0);
      const totalJpegs = relatedStats.reduce((sum, s) => sum + s.total_jpegs, 0);
      const totalPackages = relatedStats.reduce((sum, s) => sum + s.total_packages, 0);
      const totalValue = relatedStats.reduce((sum, s) => sum + s.total_tiff * s.digital_resource_value, 0);
      const progress = cluster.expected_images > 0 ? Math.min(100, (totalTiff / cluster.expected_images) * 100) : null;
      return { cluster, totalTiff, totalObjects, totalJpegs, totalPackages, totalValue, progress };
    });
  }, [clusters, globalStats]);

  // Filter cantiere cards by selected cluster
  const filteredCantieri = useMemo(() => {
    if (selectedClusterFilter === 'all') return cantieri;
    if (selectedClusterFilter === 'none') return cantieri.filter(c => !c.cluster_id);
    const clusterId = Number(selectedClusterFilter);
    return cantieri.filter(c => c.cluster_id === clusterId);
  }, [cantieri, selectedClusterFilter]);

  useEffect(() => {
    fetchUserCantieri();
    fetchGlobalStats();
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      const data = await api.get('/user/clusters');
      setClusters(data.clusters || []);
    } catch (error: any) {
      console.error('Failed to fetch clusters', error);
    }
  };

  const fetchUserCantieri = async () => {
    try {
      const data = await api.get('/user/cantieri');
      setCantieri(data.cantieri);

      // Fetch stats for each cantiere individually
      data.cantieri.forEach((c: Cantiere) => {
        fetchSingleCantiereStats(c.id);
      });
    } catch (error: any) {
      notify(error.message || 'Failed to fetch cantieri.', 'error');
    }
  };

  const fetchSingleCantiereStats = async (id: number) => {
    try {
      const stats = await api.get(`/user/cantiere/${id}/stats`);
      setCantieri(prev => prev.map(c => c.id === id ? { ...c, stats } : c));

      // Also update globalStats if it exists for this cantiere
      setGlobalStats(prev => prev.map(gs => gs.id === id ? { ...gs, ...convertStatsToGlobal(stats, gs.name, gs.id, stats.cluster_id, stats.cluster_name) } : gs));
    } catch (e) {
      console.error(`Failed to fetch stats for cantiere ${id}`, e);
    }
  };

  const convertStatsToGlobal = (stats: any, name: string, id: number | string, cluster_id?: number | null, cluster_name?: string | null): GlobalStats => {
    return {
      id,
      name,
      cluster_id,
      cluster_name,
      total_objects: Object.values(stats?.status_counts || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0),
      total_files: (stats?.total_tiff || 0) + (stats?.total_raf || 0),
      total_tiff: stats?.total_tiff || 0,
      total_raf: stats?.total_raf || 0,
      total_jpegs: stats?.total_jpeg || 0,
      total_packages: stats?.total_packages || 0,
      total_prototypes: stats?.total_prototypes || 0,
      total_sent: stats?.total_sent || 0,
      total_archived: stats?.total_archived || 0,
      digital_resource_value: stats?.digital_resource_value || 0.15
    };
  }

  const fetchGlobalStats = async () => {
    try {
      const data = await api.get('/user/global-stats');
      const initialGlobal = data.cantieri.map((c: any) => {
        const stats = c.stats;
        return {
          id: c.id,
          name: c.name,
          cluster_id: c.cluster_id,
          cluster_name: c.cluster_name,
          total_objects: stats ? Object.values(stats.status_counts || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0) : 0,
          total_files: stats ? (stats.total_tiff || 0) + (stats.total_raf || 0) : 0,
          total_tiff: stats?.total_tiff || 0,
          total_raf: stats?.total_raf || 0,
          total_jpegs: stats?.total_jpeg || 0,
          total_packages: stats?.total_packages || 0,
          total_prototypes: stats?.total_prototypes || 0,
          total_sent: stats?.total_sent || 0,
          total_archived: stats?.total_archived || 0,
          digital_resource_value: stats?.digital_resource_value || 0.15
        };
      });
      setGlobalStats(initialGlobal);

      data.cantieri.forEach((c: any) => {
        if (c.is_local) {
          fetchSingleCantiereStats(c.id);
        }
      });
    } catch (error: any) {
      console.error("Failed to fetch global stats", error);
    }
  };

  const totalStats = globalStats.reduce((acc, curr) => ({
    objects: acc.objects + curr.total_objects,
    files: acc.files + curr.total_files,
    tiffs: acc.tiffs + curr.total_tiff,
    rafs: acc.rafs + curr.total_raf,
    jpegs: acc.jpegs + curr.total_jpegs,
    packages: acc.packages + curr.total_packages,
    prototypes: acc.prototypes + curr.total_prototypes,
    sent: acc.sent + curr.total_sent,
    archived: acc.archived + curr.total_archived,
    total_val: acc.total_val + (curr.total_tiff * curr.digital_resource_value)
  }), { objects: 0, files: 0, tiffs: 0, rafs: 0, jpegs: 0, packages: 0, prototypes: 0, sent: 0, archived: 0, total_val: 0 });

  const formatEuro = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2>Benvenuto nel tuo Dashboard</h2>
        {clusters.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="cluster-filter" style={{ fontSize: '0.85rem', fontWeight: '600', color: '#555' }}>Filtra per Cluster:</label>
            <select
              id="cluster-filter"
              value={selectedClusterFilter}
              onChange={e => setSelectedClusterFilter(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', background: 'white', cursor: 'pointer' }}
            >
              <option value="all">Tutti i cantieri</option>
              {clusters.map(cl => (
                <option key={cl.id} value={String(cl.id)}>{cl.name}</option>
              ))}
              <option value="none">Senza cluster</option>
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
        {filteredCantieri.map((cantiere) => (
          <div key={cantiere.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ color: 'var(--primary-color)', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {cantiere.name}
              {!cantiere.can_edit && (
                <span style={{ fontSize: '0.65rem', background: '#f5f5f5', color: '#666', padding: '2px 6px', borderRadius: '4px', border: '1px solid #ddd' }}>
                  SOLO LETTURA
                </span>
              )}
            </h3>
            {cantiere.cluster_name && (
              <p style={{ fontSize: '0.72rem', color: '#00695c', fontWeight: '600', marginBottom: '4px' }}>
                <i className="fas fa-layer-group" style={{ marginRight: '4px' }} />{cantiere.cluster_name}
              </p>
            )}
            <p style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)', marginBottom: '12px' }}>ID: {cantiere.id}</p>

            <div style={{ flexGrow: 1 }}>
              <PieChart data={cantiere.stats?.status_counts || {}} />
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: '20px', width: '100%' }}
              onClick={() => onSelectCantiere(cantiere)}
            >
              Sfoglia Cantiere
            </button>
          </div>
        ))}
        {filteredCantieri.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#aaa', fontSize: '0.95rem' }}>
            Nessun cantiere trovato per il filtro selezionato.
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '24px', padding: '24px' }}>
        <h3 style={{ marginBottom: '16px', borderBottom: '1px solid #eee', paddingBottom: '8px', color: 'var(--primary-color)' }}>
          Legenda Stati Oggetti Digitali
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          <div>
            <strong style={{ color: '#e57373' }}>- Non Lottizzato</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Lotto non ancora assegnato all'oggetto.</p>
          </div>
          <div>
            <strong style={{ color: '#bdbdbd' }}>- Draft</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Metadati definiti (almeno Logical ID) ma assenza di file JPG nella cartella JPEG.</p>
          </div>
          <div>
            <strong style={{ color: '#ffb74d' }}>- In lavorazione</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Metadati presenti e caricamento/generazione file JPG iniziato.</p>
          </div>
          <div>
            <strong style={{ color: '#81c784' }}>- Completo</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Metadati e file JPG completi (pari ai TIFF). Generati file EXIF, ordinamenti e label assegnate.</p>
          </div>
          <div>
            <strong style={{ color: '#2e7d32' }}>- Collaudato</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Oggetto validato con successo dai sistemi Mets-Ecomic (Esito positivo).</p>
          </div>
          <div>
            <strong style={{ color: '#b00020' }}>- Errore Mets-Ecomic</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Validazione fallita. Consultare il report errori per i dettagli.</p>
          </div>
          <div>
            <strong style={{ color: '#1976d2' }}>- Pacchettizzato</strong>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Oggetto inserito in un pacchetto di versamento finale.</p>
          </div>
        </div>
      </div>

      {/* === CLUSTER STATISTICS === */}
      {clusterStats.length > 0 && (
        <div style={{ marginTop: '40px', borderTop: '2px solid #e0f2f1', paddingTop: '24px' }}>
          <h3 style={{ marginBottom: '20px', color: '#00695c' }}>
            <i className="fas fa-layer-group" style={{ marginRight: '10px' }} />
            Statistiche per Cluster
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {clusterStats.map(({ cluster, totalTiff, totalObjects, totalJpegs, totalPackages, totalValue, progress }) => (
              <div key={cluster.id} className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ margin: 0, color: '#00695c', fontSize: '1.05rem' }}>{cluster.name}</h4>
                    {cluster.description && (
                      <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#888' }}>{cluster.description}</p>
                    )}
                    <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {cluster.cantieri.map(c => (
                        <span key={c.id} style={{ fontSize: '0.72rem', background: '#e0f2f1', color: '#00695c', padding: '2px 7px', borderRadius: '4px', fontWeight: '500' }}>{c.name}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>
                    <div><strong>{totalObjects}</strong> oggetti &nbsp;|&nbsp; <strong>{totalTiff}</strong> TIFF &nbsp;|&nbsp; <strong>{totalJpegs}</strong> JPEG &nbsp;|&nbsp; <strong>{totalPackages}</strong> pacch.</div>
                    {isAdmin && <div style={{ color: 'var(--primary-color)', fontWeight: '600', marginTop: '2px' }}>{formatEuro(totalValue)} €</div>}
                  </div>
                </div>

                {/* Progress bar: TIFF vs expected */}
                {cluster.expected_images > 0 && progress !== null ? (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#666', marginBottom: '5px' }}>
                      <span>Avanzamento scansioni: <strong>{totalTiff.toLocaleString()}</strong> / <strong>{cluster.expected_images.toLocaleString()}</strong> immagini attese</span>
                      <span style={{ fontWeight: '700', color: progress >= 100 ? '#2e7d32' : progress >= 60 ? '#f57c00' : '#c62828' }}>
                        {progress.toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ background: '#e0e0e0', borderRadius: '8px', height: '10px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${progress}%`,
                        borderRadius: '8px',
                        background: progress >= 100 ? '#2e7d32' : progress >= 60 ? '#f57c00' : '#c62828',
                        transition: 'width 0.6s ease'
                      }} />
                    </div>
                  </div>
                ) : cluster.expected_images === 0 ? (
                  <p style={{ fontSize: '0.78rem', color: '#aaa', margin: 0 }}>Nessun numero di immagini attese configurato.</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === GENERAL STATISTICS === */}
      <div style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Statistiche Generali</h3>

        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '400px', overflowX: 'auto' }}>
            <table className="table-responsive">
              <thead>
                <tr style={{ background: '#e0f7fa' }}>
                  <th>Totale Complessivo</th>
                  <th>{totalStats.objects} Oggetti</th>
                  <th>{totalStats.tiffs} TIFF</th>
                  <th>{totalStats.rafs} RAF</th>
                  <th>{totalStats.jpegs} JPEG</th>
                  <th>{totalStats.packages} P. ({totalStats.prototypes} pr. | {totalStats.sent} inv. | {totalStats.archived} arch.)</th>
                  {isAdmin && <th style={{ color: 'var(--primary-color)' }}>{formatEuro(totalStats.total_val)} €</th>}
                </tr>
                <tr>
                  <th>Cantiere</th>
                  <th>Ogg. Digitali</th>
                  <th>File TIFF</th>
                  <th>File RAF</th>
                  <th>File JPEG</th>
                  <th>Pacch. (Pr | Inv | Arch)</th>
                  {isAdmin && <th>Valore Totale</th>}
                </tr>
              </thead>
              <tbody>
                {globalStats.map((stat, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{stat.name}</strong>
                      {stat.cluster_name && (
                        <div style={{ fontSize: '0.7rem', color: '#00695c', marginTop: '2px' }}>
                          <i className="fas fa-layer-group" style={{ marginRight: '3px' }} />{stat.cluster_name}
                        </div>
                      )}
                    </td>
                    <td>{stat.total_objects}</td>
                    <td>{stat.total_tiff}</td>
                    <td>{stat.total_raf}</td>
                    <td>{stat.total_jpegs}</td>
                    <td>{stat.total_packages} ({stat.total_prototypes} | {stat.total_sent} | {stat.total_archived})</td>
                    {isAdmin && <td style={{ fontWeight: 'bold' }}>{formatEuro(stat.total_tiff * stat.digital_resource_value)} €</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;